import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MoreVertical, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { printPricingCatalog } from "./components/PrintView";
import PricingCatalogSkeleton from "./components/PricingCatalogSkeleton";
import {
  CATALOG_COLUMN_COUNT,
  columnsOrderKey,
  columnsToFlat,
  groupsToColumnsByGridIndex,
  moveGroupInColumns,
  moveGroupToColumnAt,
  readColumnLayout,
  saveColumnLayout,
  toGridIndex,
} from "./utils/catalogColumns";
import { normalizeCatalogGroups, formatVariantPart, resolveProductName } from "./utils/catalogMappers";
import {
  applyThemeMapToColumns,
  buildInitialThemeMap,
  resolveGroupThemeKey,
} from "./utils/catalogThemes";
import { useDebounce } from "../hooks/useDebounce";
import { getProductsList } from "../services/product-api";
import {
  addProductsToPriceList,
  createPriceList,
  deletePriceList,
  fetchPricingCatalogGroups,
  removeProductsFromPriceList,
  updatePriceList,
  updatePricingCatalogGroupOrder,
  updatePricingCatalogProductOrder,
} from "../services/pricing-catalog-api";

import "../style/pricing-catalog.css";
import CatalogIcon from "../assets/catalog.svg";
import DeleteModalIcon from "../assets/delete-modal-icon.svg";
import FilePdf from "../assets/file-pdf.svg";
import FileExcel from "../assets/microsoft-excel-logo.svg";
import FileCsv from "../assets/file-csv.svg";

const extractApiErrorMessage = (error) =>
  error.response?.data?.message ||
  error.response?.data?.detail ||
  (Array.isArray(error.response?.data?.non_field_errors)
    ? error.response.data.non_field_errors[0]
    : null) ||
  error.message ||
  "Request failed";

const emptyGroupColumns = () =>
  Array.from({ length: CATALOG_COLUMN_COUNT }, () => []);

const cloneGroupColumns = (columns) =>
  columns.map((col) =>
    col.map((g) => {
      if (g == null) return null;
      return {
        ...g,
        products: g.products?.map((product) => ({ ...product })) ?? [],
      };
    })
  );

const productsOrderKey = (products = []) =>
  products.map((product) => product.id).join("-");

const isSameId = (a, b) => String(a) === String(b);

const findGroupInColumns = (columns, groupId) =>
  columns.flat().find((group) => group != null && isSameId(group.id, groupId));

const getGridPositionIndex = (rowIndex, columnIndex) =>
  toGridIndex(rowIndex, columnIndex);

const columnsToPositionedGroups = (columns = []) => {
  const maxLen = Math.max(0, ...columns.map((col) => col.length));
  const result = [];
  for (let row = 0; row < maxLen; row += 1) {
    for (let col = 0; col < columns.length; col += 1) {
      if (columns[col][row]) {
        result.push({
          ...columns[col][row],
          // Always persist as string grid index ("62"), never a number
          index: String(getGridPositionIndex(row, col)),
        });
      }
    }
  }
  return result;
};

const getNextPriceListIndex = (columns = emptyGroupColumns()) => {
  const targetColumnIndex = columns.reduce(
    (minIdx, col, i, arr) => (col.length < arr[minIdx].length ? i : minIdx),
    0
  );
  return getGridPositionIndex(columns[targetColumnIndex]?.length ?? 0, targetColumnIndex);
};

const getNextProductIndex = (products = []) => {
  const maxIndex = products.reduce((max, product, i) => {
    const value = Number(product.index ?? i + 1);
    return Number.isFinite(value) ? Math.max(max, value) : max;
  }, 0);
  return maxIndex + 1;
};

const getProductSearchDisplay = (product) => {
  const name = resolveProductName(product) || "Product";
  const variantParts = [
    product.ram,
    product.storage,
    ...(product.variants?.map((variant) => formatVariantPart(variant)) || []),
    product.suffix,
    product.used_mobile !== undefined && product.used_mobile !== null
      ? product.used_mobile === false
        ? "New"
        : "Used"
      : null,
  ]
    .map((part) => formatVariantPart(part))
    .filter(Boolean);

  return {
    name,
    variant: variantParts.join(" | "),
  };
};

const formatProductSearchLabel = (product) => {
  const { name, variant } = getProductSearchDisplay(product);
  return variant ? `${name} (${variant})` : name;
};

const groupThemePalette = [
  {
    key: "sky",
    vars: {
      "--group-header-bg": "#edf5ff",
      "--group-header-border": "#cfe1ff",
      "--group-header-text": "#2563eb",
      "--group-header-menu-border": "#cfe1ff",
      "--group-header-menu-bg": "#ffffff",
      "--group-header-menu-color": "#2563eb",
    },
  },
  {
    key: "mint",
    vars: {
      "--group-header-bg": "#ecfbf3",
      "--group-header-border": "#c9f0dd",
      "--group-header-text": "#059669",
      "--group-header-menu-border": "#c9f0dd",
      "--group-header-menu-bg": "#ffffff",
      "--group-header-menu-color": "#059669",
    },
  },
  {
    key: "amber",
    vars: {
      "--group-header-bg": "#fff7ea",
      "--group-header-border": "#f7dfb1",
      "--group-header-text": "#d97706",
      "--group-header-menu-border": "#f7dfb1",
      "--group-header-menu-bg": "#ffffff",
      "--group-header-menu-color": "#d97706",
    },
  },
  {
    key: "rose",
    vars: {
      "--group-header-bg": "#fff0f4",
      "--group-header-border": "#f7c8d6",
      "--group-header-text": "#e11d48",
      "--group-header-menu-border": "#f7c8d6",
      "--group-header-menu-bg": "#ffffff",
      "--group-header-menu-color": "#e11d48",
    },
  },
  {
    key: "violet",
    vars: {
      "--group-header-bg": "#f4efff",
      "--group-header-border": "#d9cdfd",
      "--group-header-text": "#7c3aed",
      "--group-header-menu-border": "#d9cdfd",
      "--group-header-menu-bg": "#ffffff",
      "--group-header-menu-color": "#7c3aed",
    },
  },
  {
    key: "teal",
    vars: {
      "--group-header-bg": "#ecfbfc",
      "--group-header-border": "#bce9eb",
      "--group-header-text": "#0f766e",
      "--group-header-menu-border": "#bce9eb",
      "--group-header-menu-bg": "#ffffff",
      "--group-header-menu-color": "#0f766e",
    },
  },
];

const getGroupThemeVars = (group) => {
  const themeKey = resolveGroupThemeKey(group);
  const matchedTheme = groupThemePalette.find((theme) => theme.key === themeKey);
  return matchedTheme ? matchedTheme.vars : groupThemePalette[0].vars;
};

const formatPrice = (symbol, value) => {
  if (value === null || value === undefined || value === "") return "";
  return `${symbol}${Number(value).toFixed(2)}`;
};

const HOLD_DELAY = 300; // ms before drag activates

const PricingCatalog = () => {
  const [groupColumns, setGroupColumns] = useState(emptyGroupColumns);
  const groups = useMemo(() => columnsToFlat(groupColumns), [groupColumns]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [orderSaving, setOrderSaving] = useState(false);
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [productFormGroupId, setProductFormGroupId] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [productName, setProductName] = useState("");
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [openMenuGroupId, setOpenMenuGroupId] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [groupFormSubmitting, setGroupFormSubmitting] = useState(false);
  const [productFormSubmitting, setProductFormSubmitting] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const debouncedProductSearch = useDebounce(productSearchQuery, 300);

  // drag state
  const [draggingGroupId, setDraggingGroupId] = useState(null);
  const [draggingRowKey, setDraggingRowKey] = useState(null); // { groupId, productId }

  const groupDragRef = useRef(null);   // { groupId, holdTimer, active, orderAtStart, orderChanged }
  const rowDragRef = useRef(null);     // { groupId, productId, holdTimer, active, orderChanged }
  const groupColumnsRef = useRef(emptyGroupColumns());
  const orderSaveInFlightRef = useRef(false);
  const productSearchOpenRef = useRef(false);

  // ── helpers ──────────────────────────────────────────────────────────────

  useEffect(() => {
    groupColumnsRef.current = groupColumns;
  }, [groupColumns]);

  const loadCatalogGroups = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const apiGroups = await fetchPricingCatalogGroups();
      const normalized = normalizeCatalogGroups(apiGroups);
      if (!normalized.length) {
        setGroupColumns(emptyGroupColumns());
      } else {
        const stored = readColumnLayout();
        // Themes can stay local; column placement always follows API grid index
        // (11 = row1/col1, 12 = row1/col2, 13 = row1/col3, 21 = row2/col1, …).
        const themeById = stored?.themes ?? buildInitialThemeMap(normalized);
        const columns = groupsToColumnsByGridIndex(normalized);
        const withThemes = applyThemeMapToColumns(columns, themeById);
        setGroupColumns(withThemes);
        saveColumnLayout(withThemes);
      }
    } catch (error) {
      console.error("Pricing catalog load failed:", error);
      toast.error(extractApiErrorMessage(error) || "Failed to load pricing catalog.");
      setGroupColumns(emptyGroupColumns());
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalogGroups();
  }, [loadCatalogGroups]);

  const fetchProductSuggestions = useCallback(async (search = "") => {
    setProductSearchLoading(true);
    try {
      const response = await getProductsList({
        search,
        page: 1,
        page_size: 20,
        only_tag: 0,
      });
      setProductSearchResults(response?.results ?? []);
    } catch (error) {
      console.error("Product search failed:", error);
      setProductSearchResults([]);
    } finally {
      setProductSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    productSearchOpenRef.current = productSearchOpen;
  }, [productSearchOpen]);

  useEffect(() => {
    if (productFormGroupId === null || !productSearchOpenRef.current) return undefined;
    let cancelled = false;
    (async () => {
      setProductSearchLoading(true);
      try {
        const response = await getProductsList({
          search: debouncedProductSearch,
          page: 1,
          page_size: 20,
          only_tag: 0,
        });
        if (cancelled) return;
        setProductSearchResults(response?.results ?? []);
      } catch (error) {
        if (!cancelled) {
          console.error("Product search failed:", error);
          setProductSearchResults([]);
        }
      } finally {
        if (!cancelled) setProductSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedProductSearch, productFormGroupId]);

  const persistGroupOrder = useCallback(
    async (
      orderedColumns,
      previousColumns = [],
      layoutSnapshot = null,
      rollbackSnapshot = null
    ) => {
      // Both sides use packed visual slots so we only PATCH groups that
      // actually moved (not mass-renumber untouched sparse API indexes).
      const orderedGroups = columnsToPositionedGroups(orderedColumns);
      const previousGroups = columnsToPositionedGroups(previousColumns);
      if (!orderedGroups.length) return;
      const nextLayout = layoutSnapshot ?? orderedColumns;
      const previousLayout = rollbackSnapshot ?? nextLayout;
      setOrderSaving(true);
      try {
        await updatePricingCatalogGroupOrder(orderedGroups, previousGroups);
        saveColumnLayout(nextLayout);
        setGroupColumns((cols) => {
          const indexById = new Map(
            orderedGroups.map((group) => [String(group.id), group.index])
          );
          const updated = cols.map((col) =>
            col.map((group) => {
              if (group == null) return null;
              const index = indexById.get(String(group.id));
              return index != null && index !== ""
                ? { ...group, index: String(index) }
                : group;
            })
          );
          groupColumnsRef.current = updated;
          return updated;
        });
      } catch (error) {
        console.error("Pricing catalog order save failed:", error);
        toast.error(extractApiErrorMessage(error) || "Failed to save group order.");
        groupColumnsRef.current = previousLayout;
        setGroupColumns(previousLayout);
        saveColumnLayout(previousLayout);
      } finally {
        setOrderSaving(false);
      }
    },
    []
  );

  const persistProductOrder = useCallback(
    async (groupId, orderedProducts, previousProducts = [], columnsSnapshot = null) => {
      if (!groupId || !orderedProducts.length) return;
      setOrderSaving(true);
      try {
        await updatePricingCatalogProductOrder(groupId, orderedProducts, previousProducts);
        setGroupColumns((cols) => {
          const nextColumns = cols.map((col) =>
            col.map((group) => {
              if (group == null) return null;
              if (!isSameId(group.id, groupId)) return group;
              return {
                ...group,
                products: group.products.map((product, i) => ({
                  ...product,
                  index: String(i + 1),
                })),
              };
            })
          );
          groupColumnsRef.current = nextColumns;
          return nextColumns;
        });
      } catch (error) {
        console.error("Pricing catalog product order save failed:", error);
        toast.error(extractApiErrorMessage(error) || "Failed to save product order.");
        if (columnsSnapshot) {
          groupColumnsRef.current = columnsSnapshot;
          setGroupColumns(columnsSnapshot);
        } else {
          await loadCatalogGroups();
        }
      } finally {
        setOrderSaving(false);
      }
    },
    [loadCatalogGroups]
  );

  const resetGroupForm = () => {
    setGroupName("");
    setGroupFormOpen(false);
    setEditingGroupId(null);
  };
  const resetProductForm = () => {
    productSearchOpenRef.current = false;
    setProductName("");
    setProductSearchOpen(false);
    setProductSearchQuery("");
    setProductFormGroupId(null);
    setSelectedProducts([]);
    setProductSearchResults([]);
    setProductSearchLoading(false);
  };

  const handleAddGroup = async () => {
    const name = groupName.trim();
    if (!name) {
      toast.error("Group name is required.");
      return;
    }

    if (editingGroupId) {
      const existing = groups.find((g) => isSameId(g.id, editingGroupId));
      setGroupFormSubmitting(true);
      try {
        await updatePriceList(editingGroupId, {
          name,
          index: existing?.index ?? getNextPriceListIndex(groupColumnsRef.current),
        });
        toast.success("Product group updated.");
        resetGroupForm();
        await loadCatalogGroups();
      } catch (error) {
        console.error("Update price list failed:", error);
        toast.error(extractApiErrorMessage(error) || "Failed to update product group.");
      } finally {
        setGroupFormSubmitting(false);
      }
      return;
    }

    setGroupFormSubmitting(true);
    try {
      await createPriceList({
        name,
        index: getNextPriceListIndex(groupColumnsRef.current),
      });
      toast.success("Product group created.");
      resetGroupForm();
      await loadCatalogGroups();
    } catch (error) {
      console.error("Create price list failed:", error);
      toast.error(extractApiErrorMessage(error) || "Failed to create product group.");
    } finally {
      setGroupFormSubmitting(false);
    }
  };

  const handleEditGroup = (group) => {
    setGroupName(group.name);
    setEditingGroupId(group.id);
    setGroupFormOpen(true);
    setOpenMenuGroupId(null);
  };

  const handleAddProduct = async () => {
    if (!productFormGroupId) {
      toast.error("Select a group first.");
      return;
    }
    if (!selectedProducts.length) {
      toast.error("Select at least one product from search.");
      return;
    }

    setProductFormSubmitting(true);
    try {
      const selectedGroup = findGroupInColumns(groupColumnsRef.current, productFormGroupId);
      const nextProductIndex = getNextProductIndex(selectedGroup?.products);
      await addProductsToPriceList(
        productFormGroupId,
        selectedProducts.map((product, i) => ({
          product_id: product.id,
          index: nextProductIndex + i,
        }))
      );
      toast.success(
        selectedProducts.length > 1
          ? "Products added to price list."
          : "Product added to price list."
      );
      resetProductForm();
      await loadCatalogGroups();
    } catch (error) {
      console.error("Add products to price list failed:", error);
      toast.error(extractApiErrorMessage(error) || "Failed to add product to group.");
    } finally {
      setProductFormSubmitting(false);
    }
  };

  const handleSelectProductSuggestion = (product) => {
    const productId = product.id ?? product.product_id;
    if (productId == null) return;
    const label = formatProductSearchLabel(product);
    setProductName(label);
    setProductSearchQuery(label);
    setSelectedProducts((prev) => {
      if (prev.some((p) => p.id === productId)) return prev;
      return [...prev, { id: productId, label }];
    });
    setProductSearchOpen(false);
  };

  const handleRemoveSelectedProduct = (productId) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const handleDeleteProduct = async (groupId, productId) => {
    try {
      await removeProductsFromPriceList(groupId, [productId]);
      toast.success("Product removed from price list.");
      setItemToDelete(null);
      await loadCatalogGroups();
    } catch (error) {
      console.error("Remove product from price list failed:", error);
      toast.error(
        extractApiErrorMessage(error) || "Failed to remove product from group."
      );
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await deletePriceList(groupId);
      toast.success("Product group deleted.");
      setOpenMenuGroupId(null);
      setItemToDelete(null);
      await loadCatalogGroups();
    } catch (error) {
      console.error("Delete price list failed:", error);
      toast.error(extractApiErrorMessage(error) || "Failed to delete product group.");
    }
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete || deleteSubmitting) return;
    setDeleteSubmitting(true);
    try {
      if (itemToDelete.type === "group") {
        await handleDeleteGroup(itemToDelete.groupId);
      } else {
        await handleDeleteProduct(itemToDelete.groupId, itemToDelete.productId);
      }
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const exportCatalog = async () => {
    if (!groups.length) {
      toast.error("No product groups to export.");
      return;
    }
    try {
      await printPricingCatalog(groupColumns);
      toast.info('Print dialog opened — choose "Save as PDF" to export.');
    } catch (err) {
      console.error("Pricing catalog print failed:", err);
      toast.error("Failed to open print export.");
    }
  };

  // ── group drag (hold-to-drag — reorder up/down in column or across columns) ─

  const resolveGroupDropTarget = (clientX, clientY, groupId) => {
    const board = document.querySelector(".pricing-catalog-board-grid");
    if (!board) return null;

    const boardRect = board.getBoundingClientRect();
    if (
      clientX < boardRect.left ||
      clientX > boardRect.right ||
      clientY < boardRect.top ||
      clientY > boardRect.bottom
    ) {
      return null;
    }

    const columns = Array.from(
      document.querySelectorAll(".pricing-catalog-board-column")
    );
    if (!columns.length) return null;

    let columnEl = columns.find((column) => {
      const rect = column.getBoundingClientRect();
      return clientX >= rect.left && clientX <= rect.right;
    });

    if (!columnEl) {
      let nearest = columns[0];
      let minDistance = Infinity;
      columns.forEach((column) => {
        const rect = column.getBoundingClientRect();
        const centerX = (rect.left + rect.right) / 2;
        const distance = Math.abs(clientX - centerX);
        if (distance < minDistance) {
          minDistance = distance;
          nearest = column;
        }
      });
      columnEl = nearest;
    }

    const columnIndex = Number(columnEl.dataset.columnIndex);
    if (!Number.isFinite(columnIndex)) return null;

    const cards = Array.from(columnEl.querySelectorAll(".pricing-group-card")).filter(
      (card) => !isSameId(card.dataset.groupId, groupId)
    );

    if (!cards.length) {
      return { columnIndex, tgtId: null, after: true };
    }

    for (let i = 0; i < cards.length; i += 1) {
      const card = cards[i];
      const rect = card.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      if (clientY < midY) {
        return { columnIndex, tgtId: card.dataset.groupId, after: false };
      }
    }

    const lastCard = cards[cards.length - 1];
    return { columnIndex, tgtId: lastCard.dataset.groupId, after: true };
  };

  const dropTargetKey = (target) => {
    if (!target) return "";
    if (!target.tgtId) return `col-${target.columnIndex}-end`;
    return `col-${target.columnIndex}-${target.tgtId}-${target.after ? "after" : "before"}`;
  };

  const applyGroupDropTarget = (srcId, target) => {
    if (!target) return;
    applyGroupColumnMove(srcId, (cols) => {
      if (!target.tgtId) {
        return moveGroupToColumnAt(cols, srcId, target.columnIndex);
      }
      return moveGroupInColumns(cols, srcId, target.tgtId, target.after);
    });
  };

  const applyGroupColumnMove = useCallback((srcId, buildNextColumns) => {
    const dragState = groupDragRef.current;
    if (!dragState?.active) return;

    setGroupColumns((cols) => {
      const nextColumns = buildNextColumns(cols);
      if (columnsOrderKey(nextColumns) === columnsOrderKey(cols)) {
        return cols;
      }
      dragState.orderChanged =
        columnsOrderKey(nextColumns) !== dragState.orderAtStart;
      groupColumnsRef.current = nextColumns;
      return nextColumns;
    });
  }, []);

  const onGroupHandlePointerDown = (e, groupId) => {
    if (catalogLoading || orderSaving) return;
    e.preventDefault();
    const handleEl = e.currentTarget;
    const orderAtStart = columnsOrderKey(groupColumnsRef.current);
    const holdTimer = setTimeout(() => {
      if (!groupDragRef.current) return;
      groupDragRef.current.active = true;
      groupDragRef.current.orderAtStart = orderAtStart;
      setDraggingGroupId(groupId);
    }, HOLD_DELAY);
    groupDragRef.current = {
      groupId,
      holdTimer,
      active: false,
      orderAtStart,
      orderChanged: false,
      lastDropKey: "",
      columnsSnapshot: cloneGroupColumns(groupColumnsRef.current),
    };

    if (handleEl.setPointerCapture) {
      try {
        handleEl.setPointerCapture(e.pointerId);
      } catch {
        // ignore if capture is unavailable
      }
    }

    const onMove = (ev) => {
      if (!groupDragRef.current?.active) return;

      const target = resolveGroupDropTarget(ev.clientX, ev.clientY, groupId);
      const nextDropKey = dropTargetKey(target);
      if (!target || nextDropKey === groupDragRef.current.lastDropKey) return;

      groupDragRef.current.lastDropKey = nextDropKey;
      applyGroupDropTarget(groupId, target);
    };

    const onUp = (ev) => {
      const dragState = groupDragRef.current;
      clearTimeout(dragState?.holdTimer);
      if (handleEl.releasePointerCapture) {
        try {
          handleEl.releasePointerCapture(ev.pointerId);
        } catch {
          // ignore if capture was already released
        }
      }
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);

      groupDragRef.current = null;
      setDraggingGroupId(null);

      if (dragState?.active && dragState.orderChanged && !orderSaveInFlightRef.current) {
        const themed = applyThemeMapToColumns(
          groupColumnsRef.current,
          Object.fromEntries(
            (dragState.columnsSnapshot ?? emptyGroupColumns())
              .flat()
              .filter(Boolean)
              .map((group) => [group.id, resolveGroupThemeKey(group)])
          )
        );
        groupColumnsRef.current = themed;
        setGroupColumns(themed);
        saveColumnLayout(themed);

        orderSaveInFlightRef.current = true;
        persistGroupOrder(
          themed,
          dragState.columnsSnapshot ?? emptyGroupColumns(),
          themed,
          dragState.columnsSnapshot ?? emptyGroupColumns()
        ).finally(() => {
          orderSaveInFlightRef.current = false;
        });
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // ── row drag (vertical, hold-to-drag on catalog icon handle) ────────────

  const moveRowInColumns = (columns, groupId, srcId, tgtId, after) =>
    columns.map((col) =>
      col.map((group) => {
        if (group == null || !isSameId(group.id, groupId)) return group;
        const prods = [...group.products];
        const src = prods.findIndex((p) => isSameId(p.id, srcId));
        const tgt = prods.findIndex((p) => isSameId(p.id, tgtId));
        if (src < 0 || tgt < 0 || src === tgt) return group;
        const [moved] = prods.splice(src, 1);
        const ins = after && src < tgt ? tgt : tgt + (after ? 1 : 0);
        prods.splice(Math.min(ins, prods.length), 0, moved);
        return { ...group, products: prods };
      })
    );

  const onRowHandlePointerDown = (e, groupId, productId) => {
    if (catalogLoading || orderSaving) return;
    e.preventDefault();
    const columnsSnapshot = cloneGroupColumns(groupColumnsRef.current);
    const productsAtStart =
      findGroupInColumns(columnsSnapshot, groupId)?.products ?? [];
    const orderAtStart = productsOrderKey(productsAtStart);

    const holdTimer = setTimeout(() => {
      if (!rowDragRef.current) return;
      rowDragRef.current.active = true;
      setDraggingRowKey({ groupId, productId });
    }, HOLD_DELAY);
    rowDragRef.current = {
      groupId,
      productId,
      holdTimer,
      active: false,
      orderAtStart,
      orderChanged: false,
      productsAtStart,
      columnsSnapshot,
    };

    const onMove = (ev) => {
      if (!rowDragRef.current?.active) return;
      const card = document.querySelector(
        `.pricing-group-card[data-group-id="${CSS.escape(String(groupId))}"]`
      );
      if (!card) return;
      const rows = Array.from(card.querySelectorAll(".pricing-product-row"));
      const target = rows.find((r) => {
        const rect = r.getBoundingClientRect();
        return ev.clientY >= rect.top && ev.clientY <= rect.bottom;
      });
      if (!target) return;
      const tgtId = target.dataset.productId;
      if (isSameId(tgtId, productId)) return;
      const rect = target.getBoundingClientRect();
      const after = ev.clientY > rect.top + rect.height / 2;
      const dragState = rowDragRef.current;
      setGroupColumns((cols) => {
        const nextColumns = moveRowInColumns(cols, groupId, productId, tgtId, after);
        const nextProducts = findGroupInColumns(nextColumns, groupId)?.products ?? [];
        if (dragState) {
          dragState.orderChanged =
            productsOrderKey(nextProducts) !== dragState.orderAtStart;
        }
        groupColumnsRef.current = nextColumns;
        return nextColumns;
      });
    };

    const onUp = () => {
      const dragState = rowDragRef.current;
      clearTimeout(dragState?.holdTimer);
      rowDragRef.current = null;
      setDraggingRowKey(null);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);

      if (dragState?.active && dragState.orderChanged && !orderSaveInFlightRef.current) {
        const orderedProducts =
          findGroupInColumns(groupColumnsRef.current, groupId)?.products ?? [];
        orderSaveInFlightRef.current = true;
        persistProductOrder(
          groupId,
          orderedProducts,
          dragState.productsAtStart,
          dragState.columnsSnapshot
        ).finally(() => {
          orderSaveInFlightRef.current = false;
        });
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // ── render ───────────────────────────────────────────────────────────────

  return (
    <div className="pricing-catalog-page">
      <div className="pricing-catalog-shell">
        <div className="pricing-catalog-header">
          <div>
            <h1>Pricing Catalog</h1>
            <p>Create and export product price lists using your existing inventory data.</p>
          </div>
          <div className="pricing-catalog-actions">
            <div className="pricing-export-wrapper">
              <button
                type="button"
                className="pricing-catalog-btn pricing-catalog-btn-secondary"
                onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              >
                Export PDF <img src={FilePdf} alt="pdf" style={{ width: 16, height: 16 }} />
              </button>
              {exportDropdownOpen && (
                <div className="pricing-export-dropdown">
                  <button
                    type="button"
                    className="pricing-export-item active"
                    onClick={() => {
                      exportCatalog();
                      setExportDropdownOpen(false);
                    }}
                  >
                    <img src={FilePdf} alt="pdf" style={{ width: 20, height: 20 }} />
                    <span>PDF</span>
                  </button>
                  <button
                    type="button"
                    className="pricing-export-item"
                    onClick={() => setExportDropdownOpen(false)}
                  >
                    <img src={FileExcel} alt="excel" style={{ width: 20, height: 20 }} />
                    <span>Excel</span>
                  </button>
                  <button
                    type="button"
                    className="pricing-export-item"
                    onClick={() => setExportDropdownOpen(false)}
                  >
                    <img src={FileCsv} alt="csv" style={{ width: 20, height: 20 }} />
                    <span>CSV</span>
                  </button>
                </div>
              )}
            </div>
            <button type="button" className="pricing-catalog-btn pricing-catalog-btn-primary" onClick={() => setGroupFormOpen(true)}>
              <Plus size={16} /> Product Group
            </button>
          </div>
        </div>

        <div className="pricing-catalog-board">
          {catalogLoading ? (
            <PricingCatalogSkeleton />
          ) : groups.length === 0 ? (
            <p className="pricing-catalog-board-status">No product groups yet. Create one to get started.</p>
          ) : (
          <div className="pricing-catalog-board-grid">
          {groupColumns.map((columnGroups, colIndex) => (
            <div
              key={`catalog-col-${colIndex}`}
              className="pricing-catalog-board-column"
              data-column-index={colIndex}
            >
          {columnGroups.map((group) => (
            <section
              key={group.id}
              data-group-id={group.id}
              className={`pricing-group-card${draggingGroupId === group.id ? " pricing-group-card-dragging" : ""}`}
              style={getGroupThemeVars(group)}
            >
              <div
                className="pricing-group-drag-handle"
                onPointerDown={(e) => onGroupHandlePointerDown(e, group.id)}
              >
                <img
                  src={CatalogIcon}
                  alt="drag group"
                  className="pricing-group-drag-icon"
                />
              </div>
              <div className="pricing-group-header">
                <h2>{group.name}</h2>
                <div className="pricing-group-menu-container">
                  <button
                    type="button"
                    className="pricing-group-menu"
                    aria-label={`Options for ${group.name}`}
                    onClick={() => setOpenMenuGroupId(openMenuGroupId === group.id ? null : group.id)}
                  >
                    <MoreVertical size={16} />
                  </button>
                  {openMenuGroupId === group.id && (
                    <div className="pricing-group-menu-dropdown">
                      <button
                        type="button"
                        className="pricing-group-menu-item"
                        onClick={() => handleEditGroup(group)}
                      >
                        Edit Group
                      </button>
                      <button
                        type="button"
                        className="pricing-group-menu-item pricing-group-menu-danger"
                        onClick={() => setItemToDelete({ type: "group", groupId: group.id })}
                      >
                        Delete Group
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="pricing-group-list">
                {group.products.map((product) => {
                  const isDragging = draggingRowKey?.groupId === group.id && draggingRowKey?.productId === product.id;
                  return (
                    <div
                      key={product.id}
                      data-product-id={product.id}
                      className={`pricing-product-row${isDragging ? " pricing-product-row-dragging" : ""}`}
                    >
                      <div className="pricing-drag-handle" aria-hidden="true">
                        <img
                          src={CatalogIcon}
                          alt="drag"
                          className="pricing-drag-handle-icon"
                          onPointerDown={(e) => onRowHandlePointerDown(e, group.id, product.id)}
                        />
                      </div>
                      <div className="pricing-product-name"><strong>{product.name}</strong></div>
                      <div className="pricing-product-meta">
                        <div className="pricing-product-variant">{product.variant}</div>
                        <div className="pricing-product-price">{formatPrice(group.currencySymbol, product.price)}</div>
                      </div>
                      <div className="pricing-product-controls">
                        <button
                          type="button"
                          className="pricing-icon-btn pricing-danger"
                          onClick={() =>
                            setItemToDelete({
                              type: "product",
                              groupId: group.id,
                              productId: product.productId ?? product.id,
                            })
                          }
                          aria-label="Delete product"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                className="pricing-add-row"
                onClick={() => setProductFormGroupId(group.id)}
              >
                <Plus size={16} /> Add more product
              </button>
            </section>
          ))}
            </div>
          ))}
          </div>
          )}
          {orderSaving && (
            <p className="pricing-catalog-board-status pricing-catalog-board-status-overlay">
              Saving catalog order…
            </p>
          )}
        </div>

        {groupFormOpen && (
          <div className="pricing-modal-backdrop" onClick={resetGroupForm} role="presentation">
            <div className="pricing-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pricing-modal-header">
                <div>
                  <h3>{editingGroupId ? "Edit Product Group Name" : "Create Product Group Name"}</h3>
                  <p>
                    {editingGroupId
                      ? "Edit product name to include in your pricing catalog"
                      : "Enter product name to include in your pricing catalog"}
                  </p>
                </div>
              </div>
              <label className="pricing-field">
                <span>Product Group Name *</span>
                <input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="e.g. Samsung Asia" />
              </label>
              <div className="pricing-modal-actions">
                <button type="button" className="pricing-catalog-btn pricing-catalog-btn-secondary" onClick={resetGroupForm}>Cancel</button>
                <button
                  type="button"
                  className="pricing-catalog-btn pricing-catalog-btn-primary"
                  onClick={handleAddGroup}
                  disabled={groupFormSubmitting}
                >
                  {groupFormSubmitting
                    ? "Saving…"
                    : editingGroupId
                      ? "Save Changes"
                      : "Create Group"}
                </button>
              </div>
            </div>
          </div>
        )}

        {productFormGroupId !== null && (
          <div className="pricing-modal-backdrop" onClick={resetProductForm} role="presentation">
            <div className="pricing-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pricing-modal-header">
                <div>
                  <h3>Add Product</h3>
                  <p>Add product to the price lists.</p>
                </div>
              </div>
              <div className="pricing-product-name-field">
                <label className="pricing-field">
                  <span>Enter Product Name *</span>
                  <input
                    value={productName}
                    onChange={(e) => {
                      setProductName(e.target.value);
                      setProductSearchQuery(e.target.value);
                      setProductSearchOpen(true);
                    }}
                    onFocus={() => {
                      productSearchOpenRef.current = true;
                      setProductSearchOpen(true);
                      fetchProductSuggestions(productSearchQuery);
                    }}
                    placeholder="e.g. A71s"
                  />
                </label>
                {productSearchOpen && (
                  <div className="pricing-search-dropdown">
                    <div className="pricing-search-input-wrap">
                      <input
                        value={productSearchQuery}
                        onChange={(e) => setProductSearchQuery(e.target.value)}
                        placeholder="Search product name..."
                        autoFocus
                      />
                    </div>
                    <div className="pricing-search-options" role="listbox" aria-label="Product search results">
                      {productSearchLoading ? (
                        <div className="pricing-search-empty">Searching products…</div>
                      ) : productSearchResults.length ? (
                        productSearchResults.map((product, i) => {
                          const productId = product.id ?? product.product_id;
                          const isSelected = selectedProducts.some((p) => p.id === productId);
                          const productDisplay = getProductSearchDisplay(product);
                          return (
                            <button
                              key={productId ?? i}
                              type="button"
                              className={`pricing-search-option${isSelected ? " selected" : ""}`}
                              style={{ "--option-index": i }}
                              onClick={() => handleSelectProductSuggestion(product)}
                            >
                              <span className="pricing-search-option-frame" aria-hidden="true" />
                              <span className="pricing-search-option-copy">
                                <span className="pricing-search-option-name">
                                  {productDisplay.name}
                                </span>
                                {productDisplay.variant && (
                                  <span className="pricing-search-option-variant">
                                    {productDisplay.variant}
                                  </span>
                                )}
                              </span>
                              {isSelected && (
                                <span className="pricing-search-option-check" aria-hidden="true">
                                  ✓
                                </span>
                              )}
                            </button>
                          );
                        })
                      ) : (
                        <div className="pricing-search-empty">
                          {productSearchQuery.trim() ? "No matches found" : "No products found"}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {selectedProducts.length > 0 && (
                <div className="pricing-selected-products">
                  {selectedProducts.map(({ id, label }) => (
                    <span key={id} className="pricing-selected-product-chip">
                      {label}
                      <button
                        type="button"
                        aria-label={`Remove ${label}`}
                        onClick={() => handleRemoveSelectedProduct(id)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="pricing-modal-actions">
                <button type="button" className="pricing-catalog-btn pricing-catalog-btn-secondary" onClick={resetProductForm}>Cancel</button>
                <button
                  type="button"
                  className="pricing-catalog-btn pricing-catalog-btn-primary"
                  onClick={handleAddProduct}
                  disabled={productFormSubmitting || !selectedProducts.length}
                >
                  {productFormSubmitting ? "Adding…" : "Add Product"}
                </button>
              </div>
            </div>
          </div>
        )}

        {itemToDelete && (
          <div className="pricing-modal-backdrop" onClick={() => setItemToDelete(null)} role="presentation">
            <div className="pricing-delete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pricing-delete-modal-icon">
                <img src={DeleteModalIcon} alt="Delete" />
              </div>
              <h3>{itemToDelete.type === "group" ? "Delete Group" : "Delete Product"}</h3>
              <p>
                {itemToDelete.type === "group"
                  ? "Are you sure you want to delete this product group from this pricing catalog? This action cannot be undone.."
                  : "Are you sure you want to delete this product from this pricing catalog? This action cannot be undone."}
              </p>
              <div className="pricing-delete-modal-actions">
                <button type="button" className="pricing-catalog-btn pricing-catalog-btn-secondary" onClick={() => setItemToDelete(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="pricing-catalog-btn pricing-catalog-btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deleteSubmitting}
                >
                  {deleteSubmitting ? "Deleting…" : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingCatalog;
